import mapboxgl, { ControlPosition } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export interface ProviderMemberLayerControlConfig {
  members: {
    clusteredLayerIds: string[];
    label: string;
    checked: boolean;
    unclusteredLayerIds: string[];
  }[],
  providers: {
    layerId: string;
    label: string;
    checked: boolean;
  }[],
  onStyleChange: (style: string) => void;
  onClusteringCheckboxChange: (checked: boolean) => void;
}

class ProviderMemberLayerControl implements mapboxgl.IControl {
  private container: HTMLDivElement;

  constructor(private config: ProviderMemberLayerControlConfig) {
    this.container = document.createElement('div');
    this.config = config;
  }

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.container.className = 'mapboxgl-ctrl provider-member-layer-control__container';

    const button = document.createElement('button');
    button.className = 'mapboxgl-ctrl-icon provider-member-layer-control__button';
    button.type = 'button';

    button.addEventListener('click', () => {
      layersContainer.style.display = layersContainer.style.display === 'none' ? '' : 'none';
    });

    const layersContainer = document.createElement('div');
    layersContainer.className = 'provider-member-layer-control__layers-container';
    layersContainer.style.display = 'none';

    if (this.config.members.length > 0) {
      const membersTitle = document.createElement('span');
      membersTitle.className = 'provider-member-layer-control__title';
      membersTitle.innerHTML = 'Members';
      layersContainer.appendChild(membersTitle);
      const clusteringCheckboxId = 'provider-member-layer-control__clusteringCheckbox';

      this.config.members.forEach((layer) => {
        const label = document.createElement('label');
        label.className = 'provider-member-layer-control__label';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = 'provider-member-layer-control__checkbox-' + layer.label.replace(/\s/g, '').toLowerCase();
        checkbox.checked = layer.checked;
        checkbox.addEventListener('change', () => {
          const clusteringCheckbox = document.getElementById(clusteringCheckboxId) as HTMLInputElement;
          layer.clusteredLayerIds.forEach((layerId) => {
            map.setLayoutProperty(layerId, 'visibility', checkbox.checked && clusteringCheckbox.checked ? 'visible' : 'none');
          });
          layer.unclusteredLayerIds.forEach((layerId) => {
            map.setLayoutProperty(layerId, 'visibility', checkbox.checked && !clusteringCheckbox.checked ? 'visible' : 'none');
          });
          layer.checked = !layer.checked;

          //Check if all layers are disabled, if so, disable the cluster button.
          clusteringCheckbox.disabled = !this.config.members.some((member) => member.checked)
        });

        label.appendChild(checkbox);
        label.appendChild(document.createTextNode(layer.label));
        layersContainer.appendChild(label);
      });

      const clusteringCheckboxlabel = document.createElement('label');
      clusteringCheckboxlabel.className = 'provider-member-layer-control__label pl-2';

      const clusteringCheckbox = document.createElement('input');
      clusteringCheckbox.id = clusteringCheckboxId;
      clusteringCheckbox.type = 'checkbox';
      clusteringCheckbox.checked = false;
      clusteringCheckbox.addEventListener('change', () => {
        this.config.onClusteringCheckboxChange(clusteringCheckbox.checked);
      });

      clusteringCheckboxlabel.appendChild(clusteringCheckbox);
      clusteringCheckboxlabel.appendChild(document.createTextNode("Cluster Member Points"));
      layersContainer.appendChild(clusteringCheckboxlabel);
    }

    if (this.config.members.length > 0 && this.config.providers.length > 0) {
      const membersProvidersSeparator = document.createElement('div');
      membersProvidersSeparator.className = 'provider-member-layer-control__separator';
      layersContainer.appendChild(membersProvidersSeparator);
    }

    if (this.config.providers.length > 0) {
      const providersTitle = document.createElement('span');
      providersTitle.className = 'provider-member-layer-control__title';
      providersTitle.innerHTML = 'Providers / Facilities';
      layersContainer.appendChild(providersTitle);

      this.config.providers.forEach((layer) => {
        const label = document.createElement('label');
        label.className = 'provider-member-layer-control__label';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = layer.checked;
        checkbox.addEventListener('change', () => {
          map.setLayoutProperty(layer.layerId, 'visibility', checkbox.checked ? 'visible' : 'none');
          layer.checked = !layer.checked;
        });

        label.appendChild(checkbox);
        label.appendChild(document.createTextNode(layer.label));
        layersContainer.appendChild(label);
      });

      const providersStyleSeparator = document.createElement('div');
      providersStyleSeparator.className = 'provider-member-layer-control__separator';
      layersContainer.appendChild(providersStyleSeparator);

      const styleTitle = document.createElement('span');
      styleTitle.className = 'provider-member-layer-control__title';
      styleTitle.innerHTML = 'Map Style';
      layersContainer.appendChild(styleTitle);

      const styles = [
        { checked: true, id: 'light', label: 'Light', style: 'mapbox://styles/mapbox/light-v11' },
        { checked: false, id: 'terrain', label: 'Terrain', style: 'mapbox://styles/mapbox/outdoors-v11' }
      ];

      styles.forEach((style) => {
        const label = document.createElement('label');
        label.className = 'provider-member-layer-control__label';

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'map-style';
        radio.value = style.style;
        radio.checked = style.checked;
        radio.addEventListener('change', () => {
          this.config.onStyleChange(style.style);
        });

        label.appendChild(radio);
        label.appendChild(document.createTextNode(style.label));
        layersContainer.appendChild(label);
      });
    }

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'provider-member-layer-control__button-container';
    buttonContainer.appendChild(button);
    this.container.appendChild(buttonContainer);

    this.container.appendChild(layersContainer);
    map.getContainer().appendChild(this.container);
    return this.container;
  }

  onRemove(): void {
    this.container.parentNode?.removeChild(this.container);
    this.container = document.createElement('div');
  }

  getDefaultPosition(): ControlPosition {
    return 'top-right';
  }
}

export default ProviderMemberLayerControl;
